import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  adminSeoImportSearchDemand,
  adminSeoSearchDemandReport,
  adminSeoDuplicates,
  adminSeoMerge,
  adminSeoFix,
  adminSeoFixStatus,
} from "../lib/api";

const CLASS_HELP = {
  exploratory: "people search brand + what-it-does words → title should use those words",
  navigational: "brand-only searches, they want the official site → snippet must promise pricing / alternatives",
  striking: "page 2–3 (pos 11–30) → needs unique text (overview, pricing, alternatives) to reach page 1",
  deep: "pos 30+ → thin page or weak match; enrich or ignore",
};

export default function AdminSeo({ onEnrich }) {
  const [refreshKey, setRefreshKey] = useState(0);
  return (
    <div className="space-y-10" data-testid="admin-seo">
      <SearchDemand onEnrich={onEnrich} refreshKey={refreshKey} />
      <Duplicates onMerged={() => setRefreshKey((k) => k + 1)} />
    </div>
  );
}

function SearchDemand({ onEnrich, refreshKey }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [fix, setFix] = useState({ running: false, results: {} });
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setReport(await adminSeoSearchDemandReport());
    } catch {
      toast.error("could not load search demand report");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    Promise.resolve().then(load);
  }, [load, refreshKey]);

  const pollFix = useCallback(() => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const s = await adminSeoFixStatus();
        setFix(s);
        if (!s.running) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          if (!s.total) {
            toast.error(s.error || "fix job not found — please try again");
          } else {
            const n = Object.values(s.results || {}).filter((r) => !r.error).length;
            toast.success(`fixed ${n}/${s.total} tools`);
          }
          setSelected(new Set());
          await load();
        }
      } catch {
        /* keep polling */
      }
    }, 2500);
  }, [load]);

  useEffect(() => {
    adminSeoFixStatus()
      .then((s) => {
        setFix(s);
        if (s.running) pollFix();
      })
      .catch(() => {});
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [pollFix]);

  const runFix = async (slugs) => {
    if (!slugs.length) return;
    try {
      const r = await adminSeoFix(slugs);
      if (!r.started) {
        toast.error("a fix run is already in progress");
        return;
      }
      setFix({ running: true, total: slugs.length, processed: 0, results: {} });
      pollFix();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "could not start fix");
    }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImporting(true);
    try {
      const text = await f.text();
      const s = await adminSeoImportSearchDemand(text);
      setSummary(s);
      toast.success(`imported ${s.rows} queries → ${s.matched_tools} tools`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "import failed");
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  const rows = (report?.tools || []).filter((r) => {
    if (filter !== "all" && r.class !== filter) return false;
    if (q && !`${r.title} ${r.slug}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });
  const last = report?.last_import;
  const hasIssue = (r) =>
    r.needs_overview || r.needs_alternatives || r.needs_pricing || (r.missing_terms || []).length > 0;
  const issueRows = rows.filter(hasIssue);
  const allSelected = issueRows.length > 0 && issueRows.every((r) => selected.has(r.slug));
  const toggle = (slug) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(slug)) n.delete(slug);
      else n.add(slug);
      return n;
    });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(issueRows.map((r) => r.slug)));

  return (
    <section data-testid="seo-search-demand">
      <h2 className="text-[15px] font-bold border-b border-black pb-1">search demand (Google Search Console)</h2>
      <p className="text-[12px] text-[#444] mt-2 leading-relaxed">
        GSC → Performance → Queries tab → Export → CSV. Upload <code>Queries.csv</code>. Every query is
        mapped to a tool; the words people add after the brand become that tool&apos;s title terms
        (live immediately via <code>tool.seo</code>; static pages rebuild in the background).
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-3 text-[12px]">
        <label className="tv-btn cursor-pointer" data-testid="seo-import-label">
          {importing ? "importing…" : "[↑ upload Queries.csv]"}
          <input
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={onFile}
            disabled={importing}
            data-testid="seo-import-input"
          />
        </label>
        {last && (
          <span className="text-[#666]" data-testid="seo-last-import">
            last import {String(last.imported_at).slice(0, 10)} · {last.rows} queries · {last.matched_tools} tools ·{" "}
            {last.matched_clicks}/{last.clicks} clicks mapped
          </span>
        )}
      </div>

      {summary?.unmatched?.length > 0 && (
        <details className="mt-3 text-[12px]" data-testid="seo-unmatched">
          <summary className="cursor-pointer">
            {summary.unmatched.length} top unmatched queries (generic or tools not in the index)
          </summary>
          <ul className="mt-1 pl-4 list-disc text-[#444]">
            {summary.unmatched.map((u) => (
              <li key={u.query}>
                {u.query} — {u.impressions} impr · pos {u.position}
              </li>
            ))}
          </ul>
        </details>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2 text-[12px]">
        {["all", "exploratory", "navigational", "striking", "deep"].map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => setFilter(c)}
            className={`tv-cat-pill ${filter === c ? "active" : ""}`}
            title={CLASS_HELP[c] || "every tool with search data"}
            data-testid={`seo-filter-${c}`}
          >
            {c}
          </button>
        ))}
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="filter by tool…"
          className="border border-black px-2 py-0.5 text-[12px]"
          data-testid="seo-filter-q"
        />
        <span className="text-[#666]">{rows.length} tools</span>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3 text-[12px]" data-testid="seo-fix-bar">
        <label className="flex items-center gap-1 cursor-pointer">
          <input
            type="checkbox"
            checked={allSelected}
            onChange={toggleAll}
            disabled={issueRows.length === 0 || fix.running}
            data-testid="seo-select-all"
          />
          select all with issues ({issueRows.length})
        </label>
        <button
          type="button"
          className="tv-btn tv-btn-primary"
          disabled={selected.size === 0 || fix.running}
          onClick={() => runFix([...selected])}
          data-testid="seo-fix-selected"
        >
          {fix.running ? `fixing ${fix.processed || 0}/${fix.total || 0}…` : `[⚡ fix ${selected.size} selected]`}
        </button>
        {fix.running && fix.current && (
          <span className="text-[#666]" data-testid="seo-fix-current">
            working on {fix.current}
          </span>
        )}
        <span className="text-[#666]">
          fix = enrich + publish missing overview / alternatives / pricing, then rewrite the overview to use the
          searched terms the tool&apos;s own site supports (unsupported terms are reported, never invented).
        </span>
      </div>

      {loading ? (
        <div className="text-[13px] text-[#888] py-4">loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-[13px] text-[#888] py-4" data-testid="seo-empty">
          no search data yet — upload a Queries.csv.
        </div>
      ) : (
        <ul className="mt-3 space-y-4" data-testid="seo-report">
          {rows.map((r) => (
            <DemandRow
              key={r.slug}
              r={r}
              onEnrich={onEnrich}
              checked={selected.has(r.slug)}
              onToggle={() => toggle(r.slug)}
              onFix={() => runFix([r.slug])}
              busy={fix.running}
              result={fix.results?.[r.slug]}
              hasIssue={hasIssue(r)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function DemandRow({ r, onEnrich, checked, onToggle, onFix, busy, result, hasIssue }) {
  const draft = r.draft_unpublished || [];
  const needs = [
    r.needs_overview && (draft.includes("overview") ? "overview (drafted, unpublished)" : "overview"),
    r.needs_alternatives && (draft.includes("alternatives") ? "alternatives (drafted, unpublished)" : "alternatives"),
    r.needs_pricing && (draft.includes("pricing tiers") ? "pricing tiers (drafted, unpublished)" : "pricing tiers"),
  ].filter(Boolean);
  return (
    <li className="border-b border-[#eee] pb-3 text-[12px]" data-testid={`seo-row-${r.slug}`}>
      <div className="flex flex-wrap items-baseline gap-x-3">
        {hasIssue && (
          <input
            type="checkbox"
            checked={checked}
            onChange={onToggle}
            disabled={busy}
            className="align-middle"
            data-testid={`seo-select-${r.slug}`}
          />
        )}
        <a href={`/tool/${r.slug}`} target="_blank" rel="noreferrer" className="tv-link font-bold text-[13px]">
          {r.title}
        </a>
        <span className="text-[#444] tabular-nums">
          {r.clicks} clicks · {r.impressions} impr · {r.ctr}% ctr · pos {r.position}
        </span>
        {r.prev && r.prev.impressions > 0 && (
          <span
            className={`tabular-nums ${r.ctr > r.prev.ctr ? "text-[#166534]" : r.ctr < r.prev.ctr ? "text-[#b91c1c]" : "text-[#666]"}`}
            title={`previous import ${String(r.prev.imported_at || "").slice(0, 10)}: ${r.prev.clicks} clicks · ${r.prev.impressions} impr · ${r.prev.ctr}% ctr · pos ${r.prev.position}`}
            data-testid={`seo-delta-${r.slug}`}
          >
            ctr {r.prev.ctr}% → {r.ctr}% · pos {r.prev.position} → {r.position}
          </span>
        )}
        <span className="border border-black px-1" title={CLASS_HELP[r.class]} data-testid={`seo-class-${r.slug}`}>
          {r.class}
        </span>
        {hasIssue && (
          <button
            type="button"
            onClick={onFix}
            disabled={busy}
            className="tv-btn"
            title="enrich + publish what's missing, then weave the searched terms into the overview (site-supported only)"
            data-testid={`seo-fix-${r.slug}`}
          >
            [⚡ fix]
          </button>
        )}
        {needs.length > 0 && (
          <button
            type="button"
            onClick={() => onEnrich?.(r.slug)}
            className="underline text-[#b45309] hover:text-black"
            title={
              "open in the enrich tab (manual). overview = the longer paragraph on the tool page (public only once published); without it the page shows just the 1-line description. alternatives = the related-tools rail + /alternatives and /vs pages. pricing tiers = the verified plan list."
            }
            data-testid={`seo-enrich-${r.slug}`}
          >
            ⚠ missing {needs.join(", ")}
          </button>
        )}
      </div>
      {needs.length > 0 && (
        <div className="mt-1 text-[#666]" data-testid={`seo-needs-help-${r.slug}`}>
          {r.needs_overview &&
            (draft.includes("overview")
              ? "an overview was drafted but never published — the live page shows only the 1-line description; fix publishes it. "
              : "no overview: the live page shows only the 1-line description; fix drafts one from the tool's site and publishes it. ")}
          {r.needs_alternatives && "no alternatives: no related-tools rail, no /alternatives or /vs pages. "}
          {r.needs_pricing && "no pricing tiers: only the $-band is shown, no plan prices (fix reads them from the site if listed). "}
        </div>
      )}
      {result && <FixResult result={result} />}
      <div className="mt-1 text-[#000]">
        <span className="text-[#888]">title →</span> {r.seo_title}
      </div>
      {r.terms?.length > 0 && (
        <div className="mt-1 text-[#444]">
          <span className="text-[#888]">searched with:</span>{" "}
          {r.terms.map((t) => (
            <span key={t.term} className="mr-2">
              {t.term} <span className="text-[#888]">({t.impressions})</span>
            </span>
          ))}
        </div>
      )}
      {r.missing_terms?.length > 0 && (
        <div className="mt-1 text-[#b45309]" data-testid={`seo-missing-${r.slug}`}>
          not in page text: {r.missing_terms.join(", ")}
        </div>
      )}
      {Object.keys(r.intents || {}).length > 0 && (
        <div className="mt-1 text-[#444]">
          <span className="text-[#888]">intent:</span>{" "}
          {Object.entries(r.intents)
            .sort((a, b) => b[1] - a[1])
            .map(([k, v]) => `${k} (${v})`)
            .join(" · ")}
        </div>
      )}
      {r.typos?.length > 0 && <div className="mt-1 text-[#666]">typos: {r.typos.join(", ")}</div>}
    </li>
  );
}

function FixResult({ result }) {
  if (result.error) {
    return (
      <div className="mt-1 text-[#b91c1c]" data-testid={`seo-fix-result-${result.slug}`}>
        ✗ fix failed: {result.error}
      </div>
    );
  }
  const sm = result.still_missing || {};
  const left = [sm.overview && "overview", sm.alternatives && "alternatives", sm.pricing && "pricing tiers"].filter(Boolean);
  return (
    <div className="mt-1 text-[#166534]" data-testid={`seo-fix-result-${result.slug}`}>
      ✓ {(result.actions || []).join(" · ") || "nothing to do"}
      {result.unsupported_terms?.length > 0 && (
        <span className="text-[#666]"> · not supported by site: {result.unsupported_terms.join(", ")}</span>
      )}
      {(left.length > 0 || sm.terms?.length > 0) && (
        <span className="text-[#b45309]">
          {" "}
          · still missing: {[...left, ...(sm.terms || [])].join(", ")}
        </span>
      )}
    </div>
  );
}

function Duplicates({ onMerged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      setData(await adminSeoDuplicates());
    } catch {
      toast.error("could not load duplicates");
    }
  }, []);

  useEffect(() => {
    Promise.resolve().then(load);
  }, [load]);

  const merge = async (keep, remove) => {
    if (!window.confirm(`Merge "${remove}" into "${keep}"? ${remove} is deleted and redirects to ${keep}.`)) return;
    setBusy(remove);
    try {
      const r = await adminSeoMerge(keep, remove);
      toast.success(`merged → ${r.kept} (+${r.moved_upvotes} upvotes, filled: ${r.filled.join(", ") || "—"})`);
      await load();
      onMerged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "merge failed");
    } finally {
      setBusy("");
    }
  };

  const groups = data?.groups || [];
  return (
    <section data-testid="seo-duplicates">
      <h2 className="text-[15px] font-bold border-b border-black pb-1">
        duplicate tools <span className="text-[#888] font-normal">({groups.length} domains)</span>
      </h2>
      <p className="text-[12px] text-[#444] mt-2">
        Same registered domain listed twice = two pages competing for one brand query. Keep the richer
        one; the other is deleted, its upvotes/fields move over, and its slug redirects.
      </p>
      {groups.length === 0 ? (
        <div className="text-[13px] text-[#888] py-3" data-testid="seo-dupes-empty">no duplicates.</div>
      ) : (
        <ul className="mt-3 space-y-3 text-[12px]" data-testid="seo-dupes-list">
          {groups.map((g) => (
            <li key={g.domain} className="border border-black p-2" data-testid={`seo-dupe-${g.domain}`}>
              <div className="font-bold">{g.domain}</div>
              <ul className="mt-1 space-y-1">
                {g.tools.map((t) => (
                  <li key={t.slug} className="flex flex-wrap items-baseline gap-x-2">
                    <a href={`/tool/${t.slug}`} target="_blank" rel="noreferrer" className="tv-link">
                      {t.slug}
                    </a>
                    <span className="text-[#666]">
                      {t.status} · ▲{t.upvotes} · {t.has_overview ? "overview" : "no overview"} · {t.n_alts} alts ·{" "}
                      {t.has_pricing ? "pricing" : "no pricing"}
                    </span>
                    {g.tools
                      .filter((o) => o.slug !== t.slug)
                      .map((o) => (
                        <button
                          key={o.slug}
                          type="button"
                          disabled={busy === o.slug}
                          onClick={() => merge(t.slug, o.slug)}
                          className="tv-btn"
                          data-testid={`seo-merge-${o.slug}-into-${t.slug}`}
                        >
                          [keep this, merge {o.slug} →]
                        </button>
                      ))}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
      {data?.redirects?.length > 0 && (
        <details className="mt-3 text-[12px]" data-testid="seo-redirects">
          <summary className="cursor-pointer">{data.redirects.length} active redirects</summary>
          <ul className="mt-1 pl-4 list-disc text-[#444]">
            {data.redirects.map((r) => (
              <li key={r.from_slug}>
                /tool/{r.from_slug} → /tool/{r.to_slug}
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
